<template>
    <div class="w-full h-full max-h-full flex flex-row justify-start items-start relative" ref="containerWrapper">
        <div class="absolute top-4 right-4 flex flex-row justify-start items-start" v-if="props.editable">
            <button type="button" class="size-8 flex items-center justify-center text-slate-700 hover:text-black rounded-full hover:bg-slate-200 z-[20]" 
            @click="saveAsAbcNotation()">
                <CloudArrowUpIcon class="size-6 text-slate-700 hover:text-black" />
            </button>
        </div>
        <div class="hidden lg:flex relative h-full p-2 flex" v-resize 
        @resize="updateSize" :class="{'min-w-0 max-w-0': !editorVisible, 'min-w-[400px] max-w-[75%]': editorVisible}" :style="{width: editorWidth + 'px'}">
            <div class="flex flex-col h-full w-full max-h-full">
                <label class="text-sm text-slate-700 font-bold" v-if="editorVisible">ABC notation:</label>
                <TextArea class="w-full h-full max-h-full" 
                    v-model="abcString" 
                    @update:modelValue="redraw(true)" 
                    placeholder="Enter ABC notation" :class="{'hidden': !editorVisible}"></TextArea>
            </div>
             <div class="resizer resizer-right border-r-2 border-dashed border-blue-200">
                <div class="absolute z-20 top-1/2 -left-2 transform -translate-y-1/2" >
                    <Tooltip text="Toggle editor" position="right" class="!w-[150px]" >
                        <button type="button" class="size-6 text-slate-700 hover:text-black z-[20]"
                            @click="toggleEditor">
                                <ArrowLeftCircleIcon v-if="editorVisible" class="size-8" />
                                <ArrowRightCircleIcon v-else class="size-8 text-slate-700 hover:text-black" />
                        </button>
                    </Tooltip>
                </div>
             </div>
        </div>
        <div ref="container" class="relative flex flex-col w-full overflow-y-auto !h-full !max-h-[100%]"
            @paste="onPaste"
            :style="{width: (containerWrapperWidth - (editorVisible ? editorWidth : 0)) + 'px'}">
            <div class="flex-grow h-full max-w-full !max-h-full overflow-y-auto">
                <div id="paper"
                    class="flex-grow max-w-full h-full"
                    style="margin: 0 !important; padding-bottom: 0 !important;overflow-y: auto !important;"
                    :style="{width: (editorVisible ? paperWidth : containerWrapperWidth) + 'px'}"></div>
            </div>
            <div v-if="abcString === ''" 
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-grow flex-1 flex-col pt-4 items-center justify-center h-full text-slate-500">
                <span>Create a new song to see the music notation.</span><br>
                <span class="text-sm text-slate-500">Copy and paste your ABC notation into the editor to see the music notation.</span>
            </div>
        </div>
        <!-- <div class="flex flex-row items-center justify-start w-full"></div> -->
        <div  class="fixed bottom-10 left-1/2 opacity-10 hover:opacity-100 transition-all duration-300 transform -translate-x-1/2 z-[10] flex flex-col border border-slate-600 dark:border-gray-700 gap-1 rounded-md bg-slate-700 dark:bg-gray-800 text-white justify-center md:w-[calc(100%-500px)] p-2 shadow-lg hover:bg-slate-800 hover:shadow-md">
            <div class="w-full flex flex-row items-center justify-start gap-2">
                <div class="flex flex-row items-center justify-start gap-2 hidden">
                    <button 
                        @click="zoomOut"
                        class="bg-slate-700 hover:bg-slate-800 dark:hover:bg-gray-700 rounded-md p-2 shadow-lg transition-all duration-300 hover:shadow-xl"
                        :class="{'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white': zoomConfig<0}">
                        <MagnifyingGlassMinusIcon class="size-6 text-white dark:!text-white hover:text-white dark:hover:text-white transition-all" />
                    </button>
                    <button 
                        @click="zoomIn"
                        class="bg-slate-700 hover:bg-slate-800 dark:hover:bg-gray-700 rounded-md p-2 shadow-lg transition-all duration-300 hover:shadow-xl"
                        :class="{'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white': zoomConfig>0}">
                        <MagnifyingGlassPlusIcon class="size-6 text-white dark:!text-white hover:text-white dark:hover:text-white transition-all" />
                    </button>

                </div>  
                <div id="audio" class="flex-1 w-full"></div>
                <div class="flex-1 flex flex-row items-center justify-start max-w-[200px]">
                    <DropDownTranspose :song="{type: 'SONG', chord: transpose}" @onTranspose="onTranspose" position="top" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, computed, watch, defineEmits, defineProps, defineExpose } from 'vue'
import abcjs from 'abcjs'
import 'abcjs/abcjs-audio.css'
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, CloudArrowUpIcon } from '@heroicons/vue/24/outline'
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@heroicons/vue/24/solid'
import DropDownTranspose from '../DropDownTranspose.vue'

const container = ref(null)
const containerWrapper = ref(null)
const zoomConfig = ref(0);
const editorVisible = ref(false);

const emits = defineEmits(['save'])
const props = defineProps(['editable', 'notation'])
const zoomMap = ref({
    "-10": 0.5,
    "-9": 0.55,
    "-8": 0.6,
    "-7": 0.65,
    "-6": 0.7,
    "-5": 0.75,
    "-4": 0.8,
    "-3": 0.85,
    "-2": 0.9,
    "-1": 0.95,
    "0": 1,
    "1": 1.05,
    "2": 1.1,
    "3": 1.15,
    "4": 1.2,
    "5": 1.25,   
    "6": 1.3,
    "7": 1.35,
    "8": 1.4,
    "9": 1.45,
    "10": 1.5,
});

const editorWidth = ref(300);

const containerWrapperWidth = computed(() => {
    return containerWrapper.value?.clientWidth || 0;
});
const containerWidth = computed(() => {
    return container.value?.clientWidth || 0;
});
const zoomValue = computed(() => {
    return zoomMap.value[zoomConfig.value.toString()];
});
// const containerHeight = computed(() => {
//     return container.value?.clientHeight || 0;
// });
const paperWidth = computed(() => {
    return containerWidth.value * zoomValue.value;
});
// const paperHeight = computed(() => {
//     return containerHeight.value * zoomValue.value;
// });
const abcString = ref(``)
const transpose = ref(0);
const abcOptions = computed(() => ({
    responsive: "resize",
    visualTranspose: transpose.value,
    dragging: false,
    add_classes: true,
    selectionColor: "green",
    dragColor: "blue",
    minPadding: 0,
    padding: 0,
    paddingBottom: 0,
    maxPadding: 0

}));

function CursorControl() {
    var self = this;

    self.onReady = function() {
    };
    self.onStart = function() {
        var svg = document.querySelector("#paper svg");
        var cursor = document.createElementNS("http://www.w3.org/2000/svg", "line");
        cursor.setAttribute("class", "abcjs-cursor");
        cursor.setAttributeNS(null, 'x1', 0);
        cursor.setAttributeNS(null, 'y1', 0);
        cursor.setAttributeNS(null, 'x2', 0);
        cursor.setAttributeNS(null, 'y2', 0);
        svg.appendChild(cursor);

    };
    self.beatSubdivisions = 2;
    // self.onBeat = function(beatNumber, totalBeats, totalTime) {
        
    // };
    self.onEvent = function(ev) {
        if (ev.measureStart && ev.left === null)
            return; // this was the second part of a tie across a measure line. Just ignore it.

        var lastSelection = document.querySelectorAll("#paper svg .highlight");
        for (var k = 0; k < lastSelection.length; k++)
            lastSelection[k].classList.remove("highlight");

        for (var i = 0; i < ev.elements.length; i++ ) {
            var note = ev.elements[i];
            for (var j = 0; j < note.length; j++) {
                note[j].classList.add("highlight");
            }
        }

        var cursor = document.querySelector("#paper svg .abcjs-cursor");
        if (cursor) {
            cursor.setAttribute("x1", ev.left - 2);
            cursor.setAttribute("x2", ev.left - 2);
            cursor.setAttribute("y1", ev.top);
            cursor.setAttribute("y2", ev.top + ev.height);
        }
    };
    self.onFinished = function() {
        var els = document.querySelectorAll("svg .highlight");
        for (var i = 0; i < els.length; i++ ) {
            els[i].classList.remove("highlight");
        }
        var cursor = document.querySelector("#paper svg .abcjs-cursor");
        if (cursor) {
            cursor.setAttribute("x1", 0);
            cursor.setAttribute("x2", 0);
            cursor.setAttribute("y1", 0);
            cursor.setAttribute("y2", 0);
        }
    };
}

const synthControl = ref(null);

function load() {
    if (abcjs.synth.supportsAudio()) {
        synthControl.value = new abcjs.synth.SynthController();
        const cursorControl = new CursorControl();
        synthControl.value.load("#audio", cursorControl, {
                displayLoop: false, 
                displayRestart: true, 
                displayPlay: true, 
                displayProgress: true, 
                displayWarp: false
            });
    } else {
        document.querySelector("#audio").innerHTML = "<div class='audio-error'>Audio is not supported in this browser.</div>";
    }
    redraw(false);
}

function redraw(userAction) {
    synthControl.value.disable(true);
    var visualObj = abcjs.renderAbc("paper", abcString.value, abcOptions.value)[0];

    var midiBuffer = new abcjs.synth.CreateSynth();
    midiBuffer.init({
        visualObj: visualObj,
    }).then(function () {
        if (synthControl.value) {
            synthControl.value.setTune(visualObj, userAction, {
                    midiTranspose: transpose.value
            }).catch(function (error) {
                console.warn("Audio problem:", error);
            });
        }
    }).catch(function (error) {
        console.warn("Audio problem:", error);
    });
}

function zoomIn() {
    if(zoomConfig.value >= 10) {
        return;
    }
    zoomConfig.value = Math.min(zoomConfig.value + 2, 10);
    redraw(true);
}

function zoomOut() {
    if(zoomConfig.value <= -10) {
        return;
    }
    zoomConfig.value = Math.max(zoomConfig.value - 2, -10);
    redraw(true);
}

function onTranspose(value) {
    transpose.value = value.semitone;
    redraw(true);
}
// function clickListener(abcElem, tuneNumber, classes, analysis, drag, mouseEvent) {
//     var lastClicked = abcElem.midiPitches;
//     if (!lastClicked)
//     return;

//     abcjs.synth.playEvent(lastClicked, abcElem.midiGraceNotePitches, abcjsEditor.millisecondsPerMeasure()).then(function (response) {
//         console.log("note played");
//     }).catch(function (error) {
//         console.log("error playing note", error);
//     });
// }
const updateSize = (event) => {
    if (event.detail.width > containerWrapperWidth.value/2) {
        return;
    }
    editorWidth.value = event.detail.width;

    redraw(true);
}
const toggleEditor = () => {
    editorVisible.value = !editorVisible.value;
}
watch( () => editorVisible.value, (newValue) => {
    if (newValue) {
        editorWidth.value = 300;
    } else {
        editorWidth.value = 0;
    }
    redraw(true);
});
const onPaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    abcString.value = text;
    redraw(true);
}

const saveAsAbcNotation = () => {
    if (confirm('Are you sure you want to save the ABC notation for the current song?')) {
        emits('save', abcString.value);
    }
}

watch(() => props.notation, (newValue) => {
    abcString.value = newValue;
    console.log('abcString', abcString.value)
    redraw(true);
});

onMounted(() => {
    load();
})

defineExpose({
    updateNotation: (notation) => {
        console.log("updateNotation", notation);
        abcString.value = notation;
        redraw(true);
    }
})
</script>
<style scoped>


</style>